// Per-project unit systems (HS-60). Four Abaqus-style consistent unit sets.
// The user picks one where the project is created — the dashboard composer or
// /learn — and the choice is persisted on the project's workflow record. A
// project with none stored (one predating those selectors) takes
// DEFAULT_UNIT_SYSTEM; ProjectPage no longer raises a blocking picker for it.
// There is no change path yet (the sole writer is the workflow PUT), but the UI
// no longer claims there never will be. All values entered anywhere in the app are interpreted
// in the chosen system — there is no numeric conversion, only labels and
// defaults change. Backend solvers are unit-agnostic (consistent-units FEA).

export const DEFAULT_UNIT_SYSTEM = 'si-mm';

export const UNIT_SYSTEMS = {
  'si-m': {
    id: 'si-m',
    label: 'SI (m)',
    description: 'm · kg · s',
    units: {
      length: 'm',
      area: 'm²',
      volume: 'm³',
      force: 'N',
      stress: 'Pa',
      density: 'kg/m³',
      mass: 'kg',
      time: 's',
      temperature: '°C',
      conductivity: 'W/(m·K)',
      specificHeat: 'J/(kg·K)',
      thermalExpansion: '1/K',
      heatFlux: 'W/m²',
    },
    // Default target edge length for a fresh mesh tile (≈ 5 mm).
    meshDefaultEdgeLength: 0.005,
    meshEdgeLengthInput: { min: '0.0001', max: '0.1', step: '0.0005' },
    materialPresets: {
      Steel: { E: 210e9, nu: 0.3, rho: 7850, name: 'Steel' },
      Aluminum: { E: 70e9, nu: 0.33, rho: 2700, name: 'Aluminum' },
      Titanium: { E: 114e9, nu: 0.34, rho: 4430, name: 'Titanium' },
    },
    thermalPresets: {
      Steel: { k: 45, cp: 500, rho: 7850, alpha: 1.2e-5, Tref: 20, name: 'Steel' },
      Aluminum: { k: 237, cp: 900, rho: 2700, alpha: 2.3e-5, Tref: 20, name: 'Aluminum' },
      Titanium: { k: 22, cp: 520, rho: 4430, alpha: 8.6e-6, Tref: 20, name: 'Titanium' },
    },
  },
  'si-mm': {
    id: 'si-mm',
    label: 'SI (mm)',
    description: 'mm · tonne · s',
    units: {
      length: 'mm',
      area: 'mm²',
      volume: 'mm³',
      force: 'N',
      stress: 'MPa',
      density: 'tonne/mm³',
      mass: 'tonne',
      time: 's',
      temperature: '°C',
      // In mm·tonne·s the energy unit is N·mm = mJ, so power is mW. Conductivity
      // in mW/(mm·K) is numerically IDENTICAL to W/(m·K) — the 1e3 in energy and
      // the 1e3 in length cancel. Specific heat does not get off so lightly:
      // 1 J/(kg·K) = 1e6 mJ/(tonne·K), hence the e8-scale preset values below.
      conductivity: 'mW/(mm·K)',
      specificHeat: 'mJ/(tonne·K)',
      thermalExpansion: '1/K',
      heatFlux: 'mW/mm²',
    },
    meshDefaultEdgeLength: 5.0,
    meshEdgeLengthInput: { min: '0.1', max: '100.0', step: '0.5' },
    materialPresets: {
      Steel: { E: 210000, nu: 0.3, rho: 7.85e-9, name: 'Steel' },
      Aluminum: { E: 70000, nu: 0.33, rho: 2.7e-9, name: 'Aluminum' },
      Titanium: { E: 114000, nu: 0.34, rho: 4.43e-9, name: 'Titanium' },
    },
    thermalPresets: {
      Steel: { k: 45, cp: 5.0e8, rho: 7.85e-9, alpha: 1.2e-5, Tref: 20, name: 'Steel' },
      Aluminum: { k: 237, cp: 9.0e8, rho: 2.7e-9, alpha: 2.3e-5, Tref: 20, name: 'Aluminum' },
      Titanium: { k: 22, cp: 5.2e8, rho: 4.43e-9, alpha: 8.6e-6, Tref: 20, name: 'Titanium' },
    },
  },
  'us-in': {
    id: 'us-in',
    label: 'US (inch)',
    description: 'in · lbf·s²/in · s',
    units: {
      length: 'in',
      area: 'in²',
      volume: 'in³',
      force: 'lbf',
      stress: 'psi',
      density: 'lbf·s²/in⁴',
      mass: 'lbf·s²/in',
      time: 's',
      temperature: '°F',
      // Consistent in·lbf·s units: energy = lbf·in, power = lbf·in/s, so
      // conductivity = power/(length·temp) = lbf/(s·°F) and specific heat =
      // energy/(mass·temp) = in²/(s²·°F). Conversion from SI:
      // k x 0.1249, cp x 861.1 (1 W = 8.8508 lbf·in/s, 1 lbf·s²/in = 175.13 kg,
      // 1 K interval = 1.8 °F).
      conductivity: 'lbf/(s·°F)',
      specificHeat: 'in²/(s²·°F)',
      thermalExpansion: '1/°F',
      heatFlux: 'lbf/(in·s)',
    },
    meshDefaultEdgeLength: 0.2,
    meshEdgeLengthInput: { min: '0.005', max: '4.0', step: '0.02' },
    materialPresets: {
      Steel: { E: 30.5e6, nu: 0.3, rho: 7.34e-4, name: 'Steel' },
      Aluminum: { E: 10.2e6, nu: 0.33, rho: 2.53e-4, name: 'Aluminum' },
      Titanium: { E: 16.5e6, nu: 0.34, rho: 4.14e-4, name: 'Titanium' },
    },
    thermalPresets: {
      Steel: { k: 5.62, cp: 4.31e5, rho: 7.34e-4, alpha: 6.67e-6, Tref: 68, name: 'Steel' },
      Aluminum: { k: 29.6, cp: 7.75e5, rho: 2.53e-4, alpha: 1.28e-5, Tref: 68, name: 'Aluminum' },
      Titanium: { k: 2.75, cp: 4.48e5, rho: 4.14e-4, alpha: 4.78e-6, Tref: 68, name: 'Titanium' },
    },
  },
  'us-ft': {
    id: 'us-ft',
    label: 'US (ft)',
    description: 'ft · slug · s',
    units: {
      length: 'ft',
      area: 'ft²',
      volume: 'ft³',
      force: 'lbf',
      stress: 'lbf/ft²',
      density: 'slug/ft³',
      mass: 'slug',
      time: 's',
      temperature: '°F',
      // Same derivation as us-in. lbf/(s·°F) is length-independent, so the
      // conductivity numbers match the inch system exactly; specific heat in
      // ft²/(s²·°F) is the inch value / 144 (SI x 5.980).
      conductivity: 'lbf/(s·°F)',
      specificHeat: 'ft²/(s²·°F)',
      thermalExpansion: '1/°F',
      heatFlux: 'lbf/(ft·s)',
    },
    meshDefaultEdgeLength: 0.016,
    meshEdgeLengthInput: { min: '0.0005', max: '0.5', step: '0.002' },
    materialPresets: {
      Steel: { E: 4.39e9, nu: 0.3, rho: 15.2, name: 'Steel' },
      Aluminum: { E: 1.46e9, nu: 0.33, rho: 5.24, name: 'Aluminum' },
      Titanium: { E: 2.38e9, nu: 0.34, rho: 8.6, name: 'Titanium' },
    },
    thermalPresets: {
      Steel: { k: 5.62, cp: 2.99e3, rho: 15.2, alpha: 6.67e-6, Tref: 68, name: 'Steel' },
      Aluminum: { k: 29.6, cp: 5.38e3, rho: 5.24, alpha: 1.28e-5, Tref: 68, name: 'Aluminum' },
      Titanium: { k: 2.75, cp: 3.11e3, rho: 8.6, alpha: 4.78e-6, Tref: 68, name: 'Titanium' },
    },
  },
};

// Resolve an id (possibly null/unknown — e.g. a read-only shared project whose
// owner predates unit selection) to a unit-system object, defaulting to SI (mm),
// the convention all pre-HS-60 projects were authored in.
export function getUnitSystem(id) {
  return UNIT_SYSTEMS[id] || UNIT_SYSTEMS[DEFAULT_UNIT_SYSTEM];
}
